import { useTheme } from 'next-themes';

import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { usePrefsStore } from '@/store/prefs';

interface SettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Settings modal (design §5. 设置弹窗).
 *
 * A neu-styled card built on the shadcn `Dialog` primitive so accessibility
 * (focus trap, Esc-to-close, overlay click-out, the top-right close ✕) comes
 * for free rather than being hand-rolled. Contents:
 *
 *   - 界面主题 — a two-segment control wired to `next-themes` (浅色 / 深色).
 *     The active segment is the one matching the current `theme`; clicking a
 *     segment calls `setTheme`. `system` shows neither segment active, which is
 *     fine — picking a segment is the way out of it.
 *   - three switch rows bound to the prefs store: 新消息通知 (`notif`),
 *     提示音 (`sound`), 回车发送 (`enterSend`).
 *
 * `notif` / `sound` only persist a preference — there is no notification or
 * sound subsystem yet (see `store/prefs.ts`). `enterSend` is read live by the
 * chat Composer.
 */
export function SettingsDialog({
  open,
  onOpenChange,
}: SettingsDialogProps): React.JSX.Element {
  const { theme, setTheme } = useTheme();

  const notif = usePrefsStore((s) => s.notif);
  const sound = usePrefsStore((s) => s.sound);
  const enterSend = usePrefsStore((s) => s.enterSend);
  const setNotif = usePrefsStore((s) => s.setNotif);
  const setSound = usePrefsStore((s) => s.setSound);
  const setEnterSend = usePrefsStore((s) => s.setEnterSend);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Override the base DialogContent chrome for the neu card: 400px wide
          (shrinking on narrow viewports), 32px corners, borderless, generous
          padding, soft raised shadow, vertical stack. `aria-describedby`
          undefined silences Radix's missing-description warning (this card has
          no descriptive paragraph). */}
      <DialogContent
        aria-describedby={undefined}
        className="flex max-h-[calc(100vh-48px)] w-[400px] max-w-[calc(100vw-48px)] flex-col gap-6 overflow-y-auto rounded-[32px] border-0 bg-background p-7 shadow-neu-raised-lg sm:rounded-[32px]"
      >
        {/* Header — title only; the close ✕ is the primitive's own absolutely
            positioned button (top-right). */}
        <DialogTitle className="text-base font-extrabold text-neu-text">
          设置
        </DialogTitle>

        {/* 界面主题 — segmented light / dark control. */}
        <div className="flex items-center justify-between gap-4">
          <span className="text-[13.5px] font-extrabold text-neu-text">
            界面主题
          </span>
          <div className="flex shrink-0 gap-1 rounded-[14px] p-1 shadow-neu-inset">
            <ThemeSegment
              label="浅色"
              active={theme === 'light'}
              onClick={() => setTheme('light')}
            />
            <ThemeSegment
              label="深色"
              active={theme === 'dark'}
              onClick={() => setTheme('dark')}
            />
          </div>
        </div>

        {/* Preference switches. */}
        <SwitchRow
          title="新消息通知"
          description="收到新消息时显示桌面通知"
          checked={notif}
          onChange={setNotif}
        />
        <SwitchRow
          title="提示音"
          description="新消息到达时播放提示音"
          checked={sound}
          onChange={setSound}
        />
        <SwitchRow
          title="回车发送"
          description="按 Enter 直接发送,Shift+Enter 换行"
          checked={enterSend}
          onChange={setEnterSend}
        />
      </DialogContent>
    </Dialog>
  );
}

// ThemeSegment -------------------------------------------------------------

interface ThemeSegmentProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

/** One segment of the theme control. Active = raised + green text; inactive =
 *  flat groove with muted text. */
function ThemeSegment({ label, active, onClick }: ThemeSegmentProps): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'neu-pressable rounded-[10px] px-4 py-1.5 text-xs font-extrabold',
        active ? 'text-[#2f9e77] shadow-neu-raised-sm' : 'text-neu-sub',
      )}
    >
      {label}
    </button>
  );
}

// SwitchRow ----------------------------------------------------------------

interface SwitchRowProps {
  title: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}

/** A labelled row: extrabold title + muted description on the left, a neu
 *  toggle on the right. The switch's `aria-label` is the row title. */
function SwitchRow({
  title,
  description,
  checked,
  onChange,
}: SwitchRowProps): React.JSX.Element {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="min-w-0">
        <p className="text-[13.5px] font-extrabold text-neu-text">{title}</p>
        <p className="text-[11px] text-neu-muted">{description}</p>
      </div>
      <NeuSwitch checked={checked} onChange={onChange} label={title} />
    </div>
  );
}

// NeuSwitch ----------------------------------------------------------------

interface NeuSwitchProps {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
}

/**
 * Bespoke neumorphic toggle — there is no shadcn switch primitive. A real
 * `<button role="switch">` (keyboard-operable for free): 48×27 track that reads
 * as an inset groove when off and a green gradient when on, with a 20px round
 * knob sliding `left 4px → 24px`. Only `left` transitions, per the perf rule
 * (no layout-affecting animation).
 */
function NeuSwitch({ checked, onChange, label }: NeuSwitchProps): React.JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={cn(
        'neu-pressable relative h-[27px] w-12 shrink-0 rounded-full',
        checked ? 'neu-gradient shadow-neu-inset-sm' : 'shadow-neu-inset',
      )}
    >
      <span
        aria-hidden="true"
        className="absolute top-1/2 size-5 -translate-y-1/2 rounded-full bg-white shadow-neu-flat transition-[left] duration-[180ms]"
        style={{ left: checked ? 24 : 4 }}
      />
    </button>
  );
}
